import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FilePlus2, ScrollText } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import type { TosAcceptanceSummary } from "../types";
import { AcceptanceRowItem } from "./AcceptanceRowItem";
import { actorIdsOf, buildAcceptanceRows, liveDocumentTypeIds } from "./acceptance-rows";
import type { OfflineAcceptanceRow } from "./acceptance-rows";
import { RecordOfflineAcceptanceDialog } from "./RecordOfflineAcceptanceDialog";
import { RevokeOfflineAcceptanceDialog } from "./RevokeOfflineAcceptanceDialog";
import type { RevokeTarget } from "./RevokeOfflineAcceptanceDialog";
import type { OfflineAcceptanceSummary } from "./types";
import { useOfflineAcceptances, useRecorderNames } from "./use-offline-acceptances";

const HEADING_ID = "user-detail-tos-heading";

interface TosAcceptancesSectionProps {
  readonly userId: string;
  readonly userName: string;
  /** `tosAcceptances` of the admin user detail. */
  readonly inApp: readonly TosAcceptanceSummary[];
  /** `tosOfflineAcceptances` of the admin user detail (summary only). */
  readonly offline: readonly OfflineAcceptanceSummary[];
  /** Holder of `tos.offline.manage`: may record, revoke and read the full records. */
  readonly canManage: boolean;
}

/**
 * Compliance area of the user-detail page (ADR-0017 A6): every ToS
 * acceptance with its source (in-app vs offline). The summary comes with the
 * user detail for every admin; the full offline records — reference, note,
 * who recorded / revoked — are fetched only for managers, and the record /
 * revoke actions are shown only to them.
 */
export function TosAcceptancesSection({
  userId,
  userName,
  inApp,
  offline,
  canManage,
}: TosAcceptancesSectionProps) {
  const { t } = useTranslation();
  const full = useOfflineAcceptances(userId, canManage);
  const [recordOpen, setRecordOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<RevokeTarget | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const recordButtonRef = useRef<HTMLButtonElement>(null);
  const revokeTriggerRef = useRef<HTMLElement | null>(null);
  const revokedRef = useRef(false);

  // Defence in depth: the permissioned records reach a row only for managers,
  // whatever the query layer (or a shared cache) happens to hold.
  const details = canManage ? full.data : undefined;
  const rows = useMemo(
    () => buildAcceptanceRows({ inApp, offline, details: details ?? [] }),
    [inApp, offline, details],
  );
  const names = useRecorderNames(canManage ? actorIdsOf(rows) : []);

  const handleRevoke = (row: OfflineAcceptanceRow, trigger: HTMLElement) => {
    revokeTriggerRef.current = trigger;
    revokedRef.current = false;
    setRevokeTarget({ id: row.id, title: row.title });
    setRevokeOpen(true);
  };

  // These dialogs are controlled, with no <DialogTrigger>, so Radix has no
  // element to hand focus back to and would drop it on <body>. Place it
  // ourselves: back on the control that opened the dialog, except after a
  // revoke — that row is about to lose its Revoke button, so go to the
  // section heading.
  const handleRecordCloseAutoFocus = (event: Event) => {
    event.preventDefault();
    recordButtonRef.current?.focus();
  };
  const handleRevokeCloseAutoFocus = (event: Event) => {
    event.preventDefault();
    const trigger = revokeTriggerRef.current;
    const target = !revokedRef.current && trigger?.isConnected ? trigger : headingRef.current;
    revokedRef.current = false;
    target?.focus();
  };

  return (
    <section
      data-testid="user-detail-tos"
      aria-labelledby={HEADING_ID}
      className="space-y-4 rounded-lg border border-border bg-card p-6"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h2
          id={HEADING_ID}
          ref={headingRef}
          tabIndex={-1}
          className="brand-eyebrow flex items-center gap-2 outline-none"
        >
          <ScrollText className="h-4 w-4" aria-hidden="true" />
          {t("users.tos.title")}
        </h2>
        {canManage && (
          <Button
            ref={recordButtonRef}
            data-testid="tos-record-open"
            variant="outline"
            size="sm"
            onClick={() => setRecordOpen(true)}
          >
            <FilePlus2 className="mr-2 h-4 w-4" aria-hidden="true" />
            {t("users.tos.record")}
          </Button>
        )}
      </div>

      {canManage && full.isError && (
        <p data-testid="tos-details-error" role="alert" className="text-sm text-destructive">
          {t("users.tos.detailsError")}
        </p>
      )}

      {rows.length === 0 ? (
        <p data-testid="tos-empty" className="text-sm text-muted-foreground">
          {t("users.tos.empty")}
        </p>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border">
          {rows.map((row) => (
            <AcceptanceRowItem
              key={`${row.kind}:${row.id}`}
              row={row}
              canManage={canManage}
              names={names}
              onRevoke={handleRevoke}
            />
          ))}
        </ul>
      )}

      {canManage && (
        <>
          <RecordOfflineAcceptanceDialog
            open={recordOpen}
            onOpenChange={setRecordOpen}
            userId={userId}
            userName={userName}
            excludedTypeIds={liveDocumentTypeIds(offline)}
            onCloseAutoFocus={handleRecordCloseAutoFocus}
          />
          <RevokeOfflineAcceptanceDialog
            open={revokeOpen}
            onOpenChange={setRevokeOpen}
            userId={userId}
            userName={userName}
            target={revokeTarget}
            onRevoked={() => {
              revokedRef.current = true;
            }}
            onCloseAutoFocus={handleRevokeCloseAutoFocus}
          />
        </>
      )}
    </section>
  );
}
